import {
  Card,
  CardContent,
  Grid,
  Typography,
  CardHeader,
  Divider,
  Chip,
  Box,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useRenewalSingleQuery } from '../../rest/useFetchRenewalApplication';
import PageLoader from '../../Components/Spinner/PageLoader';
import { getStatusLabel } from '../../Utils/Label/status-label';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../Components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from '../../Components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';

import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../Components/CommonDetailItem/CommonDetailItem';

const SingleRenewalApplication = () => {
  const { id } = useParams();
  const {
    data: application,
    fetchData,
    loading,
  } = useRenewalSingleQuery(id !== undefined ? id : '');
  const [open, setOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {!loading ? (
        <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 }, mb: 3 }}>
          <Card
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  Renewal Application Details
                </Typography>
              }

              sx={{
                borderBottom: '1px solid #edf2f7',
                py: 1.25,
                px: { xs: 2, sm: 2.5 },
                backgroundColor: '#fafbfc',
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
              }}
            >
              {application ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Rejection / Note banner if any */}
                  {application?.renewal_application_reject_reason && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.5,
                        fontSize: '0.875rem',
                        '& .MuiAlert-message': { py: 0.25 },
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        component='span'
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Note / Rejection Reason:
                      </Typography>{' '}
                      {application.renewal_application_reject_reason}
                    </Alert>
                  )}

                  {/* Section 1: Application & Company Overview */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        General Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application Date'
                        value={formatDate(application?.renewal_application_created_at)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Company Name'
                        value={formatValue(application?.user_member_company_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Status'
                        value={
                          application?.renewal_application_status
                            ? getStatusLabel(application.renewal_application_status)
                            : 'N/A'
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='TIN Number'
                        value={formatValue(application?.tin_number)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='NID Number'
                        value={formatValue(application?.nid_no)}
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 2: License Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        License Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Civil Aviation No.'
                        value={formatValue(application?.civil_aviation_number)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='CA Issue Date'
                        value={formatDate(application?.civil_aviation_issue_date)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='CA Expiry Date'
                        value={formatDate(application?.civil_aviation_expiry_date)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Trade License No.'
                        value={formatValue(application?.trade_license_number)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='TL Issue Date'
                        value={formatDate(application?.trade_license_issue_date)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='TL Expiry Date'
                        value={formatDate(application?.trade_license_expiry_date)}
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 3: Uploaded Documents */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='NID Document'
                        value={
                          application?.nid_file ? (
                            <CommonTypoGraphy
                              name='View NID'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(application.nid_file);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Civil Aviation File'
                        value={
                          application?.renewal_application_updated_civil_aviation_file ? (
                            <CommonTypoGraphy
                              name='View Civil Aviation'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.renewal_application_updated_civil_aviation_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Trade License File'
                        value={
                          application?.renewal_application_updated_trade_license_file ? (
                            <CommonTypoGraphy
                              name='View Trade License'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.renewal_application_updated_trade_license_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Tax Return File'
                        value={
                          application?.renewal_application_tax_return_certificate_file ? (
                            <CommonTypoGraphy
                              name='View Tax Return'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.renewal_application_tax_return_certificate_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Prev Certificate'
                        value={
                          application?.renewal_application_prev_atab_certificate_file ? (
                            <CommonTypoGraphy
                              name='View ATAB Cert'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.renewal_application_prev_atab_certificate_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Undertaking File'
                        value={
                          application?.under_taking_file ? (
                            <CommonTypoGraphy
                              name='View Undertaking'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(application.under_taking_file);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Office Deed File'
                        value={
                          application?.office_deed_file ? (
                            <CommonTypoGraphy
                              name='View Office Deed'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(application.office_deed_file);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      {application?.ltd_docs && (
                        <CommonDetailItem
                          labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                          label='Ltd. Co. Docs'
                          value={
                            <CommonTypoGraphy
                              name='View Ltd. Docs'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(application.ltd_docs);
                              }}
                            />
                          }
                        />
                      )}
                      {application?.schedule_10_12 && (
                        <CommonDetailItem
                          labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                          label='Schedule 10/12'
                          value={
                            <CommonTypoGraphy
                              name='View Schedule 10/12'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(application.schedule_10_12);
                              }}
                            />
                          }
                        />
                      )}
                    </Grid>
                  </Box>

                  {/* Section 4: Payment Info */}
                  {application?.paymentInfo && (
                    <>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 3.5,
                              height: 15,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }}
                          />
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.925rem',
                              color: '#0f172a',
                              letterSpacing: 0.1,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Chip
                            label={
                              application?.paymentInfo?.type === 'online'
                                ? 'Online Payment'
                                : 'Offline Payment'
                            }
                            size='small'
                            color={application?.paymentInfo?.type === 'online' ? 'primary' : 'default'}
                            variant='outlined'
                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
                          />
                        </Box>
                        <OfflinePaymentCardInformation data={application?.paymentInfo} />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.secondary'
                  textAlign='center'
                  py={4}
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>

          <ModalsWrapper
            modalData={{ title: 'View Document' }}
            setShowModal={setOpen}
            showModal={open}
          >
            <PdfAndImageView file={imageFile} />
          </ModalsWrapper>
        </Box>
      ) : (
        <PageLoader />
      )}
    </>
  );
};

export default SingleRenewalApplication;

